import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { FormElement, FormRender } from '../../../../forms/FormRenderer/types'
import { BeforeSubmitHandler, OnSubmit } from '../../../../forms/TwinForm/types'
import useTwinTranslation from '../../useTwinTranslation'
import { SupplierPayload } from '../../../reducers/products/suppliers'
import CustomSelectAndCreateSuppliers from '../../../../specificComponents/Suppliers/CustomSelectAndCreateSuppliers'
import { CustomerPayload } from '../../../reducers/customers/customer'
import CustomSelectSectionCustomers from '../../../../specificComponents/Customers/CustomSelectSectionCustomers'
import { BuyOrderModelTypeExtended } from '../../../../app/erp/screens/Logistic/BuyParent/BuyOrders/ModalCreateBuyOrder/types'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../globals/dictionary'
import { BTOBillingTypeBuyKeysInversed } from '@teinor/erp/types/company/config/billingGroup/billingTypeBuy/billingTypeBuyOption'
import { twinFetchPostJSON } from '../../../globals/data'
import { getBillingGroups, getDictionaryCountries } from '../../../reducers/getters'
import { ImageInputFile } from '../../../../forms/ImageInput/types'
import { getDictionaryStates } from '../../../globals/countries'
import { sortCountries } from '../../../globals/sortCountries'
import useCountryState from '../../useCountry'
import { useTypeIdNumber } from '../../useTypeIdNumber'
import { CustomSellSelectProject } from '../../../../specificComponents/sells/components'

interface CreateBuyProps {
    suppliers: SupplierPayload
    customers: CustomerPayload
    BillingGroupId?: number
    baseURL: string
}

export type SupplierBuyInfoTy = {
    typeBilling: BTOBillingTypeBuyKeysInversed
    values: TwinDictionary
    SupplierId?: number
    CustomerId?: number
    projects: TwinDictionary | null
}

const btoBillingTypeBuy: TwinDictionary = {
    0: 'supplier',
    1: 'autoInvoice',
    2: 'supplierHeredate',
}

const useCreateBuy = ({ suppliers, customers, BillingGroupId, baseURL }: CreateBuyProps) => {
    const [specialData, setSpecialData] = useState<SupplierBuyInfoTy>({ typeBilling: 'supplierHeredate', values: {}, SupplierId: undefined, CustomerId: undefined, projects: null })
    const { billingTypesBuy, myBillingGroupId } = useMemo(()=>{
        const billingGroups = getBillingGroups()
        let myBillingGroupId = BillingGroupId || billingGroups?.defaultBillingGroupId
        return { 
            billingTypesBuy: billingGroups?.dictionaryBillingGroup?.[myBillingGroupId || 0]?.billingTypesBuyDict || {},
            myBillingGroupId
        }
    }, [BillingGroupId])
    const [file, setFile] = useState <ImageInputFile[]>([])
    const { country, setCountry } = useCountryState({ defaultCountry: '' })
    const { t } = useTwinTranslation()
    const { typesIdNumber } = useTypeIdNumber()
    

    const itemsColumns = {
        'reference': { value: 'reference', name: t('reference', 'Referencia')},
        'code_bar': { value: 'code_bar', name: t('barCode', 'Código de barras')},
        'supplier_reference': { value: 'supplier_reference', name: t('supplierRef', 'Referencia proveedor')}
    }

    const [billingGroupRel, setBillingGroupRel] = useState<number[] | null>(getDefaultBillingBuyOptions(billingTypesBuy))
    const navigate = useNavigate()

    const createBuy: OnSubmit = useCallback(async (res) => {
        const create = await res.json()
        return navigate(baseURL + '/' +  create.id)
    }, [navigate, baseURL])
    
    const getSupplierDefaulltValues = useCallback(async (SupplierId: number) => {
        if (SupplierId !== 0) {
            const myBillingGroup = await twinFetchPostJSON('/api/app/supplier/supplierBillingGroup/getBillingGroupSupplierInstance', { BillingGroupId: myBillingGroupId, SupplierId })
            setBillingGroupRel(myBillingGroup?.BillingTypeBuyOptions ? myBillingGroup.BillingTypeBuyOptions : getDefaultBillingBuyOptions(billingTypesBuy))
        } else {
            setBillingGroupRel(getDefaultBillingBuyOptions(billingTypesBuy))
        }
    }, [myBillingGroupId, setBillingGroupRel, billingTypesBuy])

    const handleSupplierInfo = useCallback(async (value: number) => {
        await getSupplierDefaulltValues(value)
        setSpecialData((old) => { return { ...old, SupplierId: value } })
    }, [setSpecialData, getSupplierDefaulltValues])

    const handleChangeInfo = useCallback(async (field: 'SupplierId' | 'CustomerId', value: string) => {
        setSpecialData((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return { ...copy, [field]: parseInt(value) }
        })
        if(field === 'SupplierId'){
            handleSupplierInfo(parseInt(value))
        }
    }, [setSpecialData, handleSupplierInfo])

    const getCustomerProjects = useCallback(async () => {
        return await twinFetchPostJSON('/api/app/customer/customerProject/getAllCustomerProjects', {
            where:
            {
                'CustomerProject.active': true,
                CustomerId: specialData.CustomerId
            },
        })
    }, [specialData.CustomerId])

    const myCustomerProjectsOnSubmit = useCallback(async () => {
        const result = await getCustomerProjects()
        if (result) {
            setSpecialData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                copy.projects = dictionaryComplexFromJsonArr(result)
                return { ...copy }
            })
        }
    }, [getCustomerProjects])

    const handleCustomerCampaignProject = useCallback(async (_id: string, fieldName: string, value: string) => {
        setSpecialData((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return { ...copy, [fieldName]: parseInt(value) }
        })
    }, [setSpecialData])

    const searchItems = useCallback(async () => {
        const searchedItems = await getCustomerProjects()
        if (searchedItems) {
            setSpecialData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                copy.projects = dictionaryComplexFromJsonArr(searchedItems)
                return { ...copy }
            })
        } else {
            setSpecialData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                return { ...copy, projects: null }
            })
        }
    }, [setSpecialData, getCustomerProjects])


    const checkChangeTypeBilling = useCallback((values: TwinDictionary): BTOBillingTypeBuyKeysInversed => {
        let result: BTOBillingTypeBuyKeysInversed = 'autoInvoice'
        for (const key in billingTypesBuy) {
            const billingTypeBuy = billingTypesBuy[key]
            const value = billingTypeBuy?.billingTypeBuyOptionsDict?.[parseInt(values[billingTypeBuy.id])]
            if (value) {
                const myTypeBilling = btoBillingTypeBuy[value.typeBilling]
                if (myTypeBilling) {
                    result = myTypeBilling
                    if (myTypeBilling !== 'supplierHeredate') {
                        break
                    }
                }
            }
        }
        return result
    }, [billingTypesBuy])

    const handleChangeTypeBilling = useCallback((id: number, value: number) => {
        setSpecialData((old) => {
            const vals = { ...old.values, [id]: value }
            return {
                ...old,
                typeBilling: checkChangeTypeBilling(vals),
                values: vals,
            }
        })
    }, [setSpecialData, checkChangeTypeBilling])


    const parseBTOBeforeSubmit: BeforeSubmitHandler = useCallback((vals) => {
        const result: number[] = []
        for (const key in billingTypesBuy) {
            const billingType = billingTypesBuy[key]
            const index = 'BTO_' + billingType.id
            const element = vals[index];
            result.push(element)
        }
        vals['BillingTypeBuyOptions'] = result
        return true
    }, [billingTypesBuy])


    const supplierInfo: FormElement<keyof BuyOrderModelTypeExtended>[] = [
        {
            name: 'SupplierId',
            component: 'Custom',
            render: ({ extraProps }) => {
                return <CustomSelectAndCreateSuppliers name='SupplierId' suppliers={extraProps.suppliers} label={t('supplier', 'Proveedor')} onChange={(value)=>handleChangeInfo('SupplierId', value)} required={true} />
            },
            extraProps: { suppliers }
        },
        {
            name: 'useSecondTax',
            component: 'CheckboxMargin',
            label: t('useSecondTax', 'Usar recargo de equivalencia')
        }
    ]
    const customerInfo: FormElement<keyof BuyOrderModelTypeExtended>[] =[]
    customerInfo.push({
            name: 'CustomerId',
            component: 'Custom',
            render: ({ extraProps }) => {
                return <CustomSelectSectionCustomers name='CustomerId' customers={extraProps.customers} label={t('customer', 'Cliente')} onChange={(value) => handleChangeInfo('CustomerId', value)} />
            },
            extraProps: { customers }
        }
    )
    if (specialData.CustomerId !== undefined && specialData.CustomerId !== 0) {
        customerInfo.push({
            name: 'CustomerProjectId',
            component: 'Custom',
            render: ({ extraProps }) => {
                return <CustomSellSelectProject getCompleteData={myCustomerProjectsOnSubmit} headerData={specialData} items={extraProps.projects} handleChange={handleCustomerCampaignProject} name='CustomerProjectId' orderByOpp={true} viewLabel={false} />
            },
            extraProps: { projects: specialData?.projects || {} }
        })
    }

    const extraBillingTypesBuy: FormElement<any>[] = []
    if (specialData.SupplierId !== undefined) {
        for (const key in billingTypesBuy) {
            const type = billingTypesBuy[parseInt(String(key))]
            let value = type.DefaultBillingTypeBuyOptionId
            if (billingGroupRel) {
                for (const element of billingGroupRel) {
                    if (element in type.billingTypeBuyOptionsDict) {
                        value = element
                    }
                }
            }
            extraBillingTypesBuy.push({
                label: type.name,
                name: 'BTO_' + type.id,
                component: 'CustomSelectWithSearchBar',
                items: type.billingTypeBuyOptionsDict || {},
                required: type.mandatory,
                value,
                onChange: (value: number) => handleChangeTypeBilling(type.id, value)
            })
        }
    }

    const fileInfo: FormElement<any>[] = [
        {
            name: 'orderFilePath',
            component: 'FilesInput',
            label: t('uploadExcelOrCSV', 'Subir excel o CSV'),
            acceptFileConditions: {
                'text': ['.csv', '.xlsx']
            },
            onChange: setFile,
        },
        {
            name: 'supplierPdf',
            component: 'FilesInput',
            label: t('supplierPDF', 'PDF del proveedor'),
            acceptFileConditions: {
                'text': ['.pdf',]
            },
        },
    ]
    if (file.length){
        fileInfo.push(
            {
                name: 'delimiter',
                label: t('delimiter', 'Delimitado'),
                component: 'InputWithLabelMargin',
                defaultValue: ';',
                required: true
            },
            {
                name: 'rowsToSkip',
                label: t('rowsToSkip', 'Filas que saltar'),
                component: 'InputWithLabelMarginIntegerStateFull',
                required: true
            },
            {
                name: 'searchByFile',
                label: t('searchBy', 'Buscar por'),
                component: 'CustomSelectWithSearchBar',
                items: itemsColumns,
                required: true
            },
            {
                name: 'posColumnSelected',
                label: t('posColumnSelected', 'Posición columna seleccionada'),
                component: 'InputWithLabelMarginIntegerStateFull',
                required: true
            },
            {
                name: 'posQuantityColumn',
                label: t('posQuantityColumn', 'Posición columna cantidad'),
                component: 'InputWithLabelMarginIntegerStateFull',
                required: true
            },
            {
                name: 'posPriceColumn',
                label: t('posPriceColumn', 'Posición columna precio'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
            {
                name: 'posNotesColumn',
                label: t('posNotesColumn', 'Posición columna notas'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
        )
    }
    const addressFields: FormRender<BuyOrderModelTypeExtended> = []
    const mySupplierInfo: FormElement<keyof BuyOrderModelTypeExtended>[] = []

    if (specialData.SupplierId === 0) {
        mySupplierInfo.push(
            {
                name: 'name',
                label: t('nameSupplier', 'Nombre del proveedor'),
                component: 'InputWithLabelMargin',
                required: true
            },
            {
                name: 'fiscal_name',
                label: t('fiscalName', 'Nombre fiscal'),
                component: 'InputWithLabelMargin',
                required: true
            },
            {
                name: 'type_id_number',
                label: t('typeIdNumber', 'Tipo de documento identificación'),
                component: 'CustomSelectWithSearchBar',
                items: typesIdNumber,
                value: 0,
            },
            {
                name: 'id_number',
                label: t('id_number', 'DNI'),
                component: 'InputWithLabelMargin',
                type: 'text',
                required: true,
            },
            {
                name: 'code',
                label: t('code', 'Código'),
                component: 'InputWithLabelMarginIntegerStateFull',
                type: 'text',
            },
        )
        addressFields.push(
            {
                cols: 1,
                title: t('addressSupplier', 'Dirección proveedor'),
                elements: [
                    {
                        name: 'address',
                        label: t('address', 'Dirección'),
                        component: 'InputWithLabelMargin',
                        type: 'text',
                        required: true
                    },
                    {
                        name: 'additional_address',
                        label: t('additional_address', 'Dirección adicional'),
                        component: 'InputWithLabelMargin',
                        type: 'text'
                    },
                    {
                        name: 'postal_code',
                        label: t('postalCode', 'Código Postal'),
                        component: 'InputWithLabelMargin',
                        type: 'text',
                        required: true
                    },
                    {
                        name: 'city',
                        label: t('city', 'Ciudad'),
                        component: 'InputWithLabelMargin',
                        type: 'text',
                        required: true
                    },
                    {
                        name: 'country',
                        label: t('country', 'País'),
                        component: 'CustomSelectWithSearchBar',
                        items: getDictionaryCountries(),
                        sortFunction: sortCountries,
                        required: true,
                        value: '',
                        onChange: (value: string) => setCountry(value)
                    },
                    {
                        name: 'state',
                        label: t('state', 'Provincia'),
                        component: 'CustomSelectWithSearchBar',
                        sortItems: true,
                        items: getDictionaryStates(country),
                        required: true,
                        value: ''
                    },
                ]
            }
        )
    }
    useEffect(() => {
        if (specialData.CustomerId) {
            searchItems()
        }
    }, [searchItems, specialData.CustomerId])

    return { extraBillingTypesBuy, parseBTOBeforeSubmit, handleChangeTypeBilling, createBuy, handleSupplierInfo, supplierInfo, customerInfo, fileInfo, addressFields, mySupplierInfo }
}


const getDefaultBillingBuyOptions = (billingTypesBuy: TwinDictionary) => {
    let result : number[] | null = null
    for (const key in billingTypesBuy) {
        const billingTypeBuy = billingTypesBuy[key]
        if (billingTypeBuy.DefaultBillingTypeBuyOptionId){
            if(!result){
                result = []
            }
            result.push(billingTypeBuy.DefaultBillingTypeBuyOptionId)
        }
    }
    return result
}

export default useCreateBuy